import { useState, useEffect, useRef } from "react";
import Layout from "./../Layout";
import { useParams } from "react-router-dom";
import {
  MapPin,
  Mountain,
  Droplet,
  Eye,
  Tag,
  Clock,
  Car,
  Calendar,
  Wallet,
  ChevronLeft,
  ChevronRight,
  CheckCircle, // Make sure CheckCircle is included here
} from "lucide-react";

const API_BASE_URL = "https://stage.beachcabana.lk/hotelmanage/api.php";
const AUTH_HEADER = {
  headers: {
    Authorization: "Basic YWRtaW46cGFzc3dvcmQ=",
  },
};

const categoryIcons = {
  Viewpoint: Eye,
  Hiking: Mountain,
  Waterfall: Droplet,
  Attraction: Eye,
  default: Tag,
};

export default function ThingsToDoSingle() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [pausedCarousel, setPausedCarousel] = useState(false);
  const carouselRef = useRef(null);

  // Fetch single thing to do from API
  useEffect(() => {
    const fetchSingleThingToDo = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}?param=thingstodosingle`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': AUTH_HEADER.headers.Authorization,
          },
          body: JSON.stringify({ slug: slug }),
        });
        const data = await response.json();
        if (data.code === 200 && data.data) {
          setItem(data.data);
        }
      } catch (error) {
        console.error("Error fetching single thing to do:", error);
      }
    };

    fetchSingleThingToDo();
  }, [slug]);

  // Auto-slide for the carousel
  useEffect(() => {
    let interval;
    if (item && item.image_urls) {
      const imageUrls = JSON.parse(item.image_urls);
      if (imageUrls.length > 1) {
        interval = setInterval(() => {
          if (!pausedCarousel) {
            setCurrentImageIndex((prev) => (prev + 1) % imageUrls.length);
          }
        }, 3000);
      }
    }
    return () => clearInterval(interval);
  }, [item, pausedCarousel]);

  // Carousel controls
  const nextSlide = () => {
    if (item && item.image_urls) {
      const imageUrls = JSON.parse(item.image_urls);
      setCurrentImageIndex((prev) => (prev + 1) % imageUrls.length);
    }
  };

  const prevSlide = () => {
    if (item && item.image_urls) {
      const imageUrls = JSON.parse(item.image_urls);
      setCurrentImageIndex((prev) => (prev - 1 + imageUrls.length) % imageUrls.length);
    }
  };

  const goToSlide = (index) => {
    setCurrentImageIndex(index);
  };

  // Touch support for carousel
  const handleTouchStart = (e) => {
    setPausedCarousel(true);
    carouselRef.current = { startX: e.targetTouches[0].clientX };
  };

  const handleTouchMove = (e) => {
    if (carouselRef.current) {
      carouselRef.current.endX = e.targetTouches[0].clientX;
    }
  };

  const handleTouchEnd = () => {
    if (carouselRef.current?.startX && carouselRef.current?.endX && item?.image_urls) {
      const distance = carouselRef.current.startX - carouselRef.current.endX;
      const minSwipeDistance = 50;
      if (Math.abs(distance) > minSwipeDistance) {
        if (distance > 0) {
          nextSlide();
        } else {
          prevSlide();
        }
      }
    }
    setPausedCarousel(false);
  };


  if (!item) {
    return <Layout pageTitle="Loading...">Loading...</Layout>;
  }

  const imageUrls = JSON.parse(item.image_urls || "[]");
  const Icon = categoryIcons[item.category] || categoryIcons.default;
  const pageTitle = item.title;

  return (
    <Layout pageTitle={pageTitle} background="false">
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Carousel Section */}
          <div
            className="relative w-full h-96"
            onMouseEnter={() => setPausedCarousel(true)}
            onMouseLeave={() => setPausedCarousel(false)}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            role="region"
            aria-label={`Carousel for ${item.title}`}
          >
            <div className="relative w-full h-full overflow-hidden rounded-t-lg">
              <div
                className="flex h-full w-full transition-transform duration-500 ease-in-out will-change-transform"
                style={{
                  transform: `translateX(-${currentImageIndex * 100}%)`,
                }}
              >
                {imageUrls.map((src, i) => (
                  <div key={i} className="w-full h-full flex-shrink-0">
                    <img
                      src={`/images/thingstodo/${src}`}
                      className="w-full h-full object-cover select-none"
                      alt={`${item.title} slide ${i + 1}`}
                      loading={i === currentImageIndex ? "eager" : "lazy"}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Indicators */}
            {imageUrls.length > 1 && (
              <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 space-x-2 z-30">
                {imageUrls.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`h-2 w-2 rounded-full ${
                      i === currentImageIndex ? "bg-white" : "bg-white/50"
                    } hover:bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-white`}
                    aria-label={`Go to image ${i + 1} for ${item.title}`}
                    aria-current={i === currentImageIndex ? "true" : "false"}
                    onClick={(e) => {
                      e.stopPropagation();
                      goToSlide(i);
                    }}
                  ></button>
                ))}
              </div>
            )}

            {/* Controls */}
            {imageUrls.length > 1 && (
              <>
                <button
                  type="button"
                  className="absolute left-0 top-0 flex h-full items-center justify-center px-3 focus:outline-none group"
                  onClick={(e) => {
                    e.stopPropagation();
                    prevSlide();
                  }}
                  aria-label={`Previous image for ${item.title}`}
                >
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/50 group-hover:bg-black/70 group-focus:ring-2 group-focus:ring-white transition-colors">
                    <ChevronLeft className="h-5 w-5 text-white" />
                  </span>
                </button>
                <button
                  type="button"
                  className="absolute right-0 top-0 flex h-full items-center justify-center px-3 focus:outline-none group"
                  onClick={(e) => {
                    e.stopPropagation();
                    nextSlide();
                  }}
                  aria-label={`Next image for ${item.title}`}
                >
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/50 group-hover:bg-black/70 group-focus:ring-2 group-focus:ring-white transition-colors">
                    <ChevronRight className="h-5 w-5 text-white" />
                  </span>
                </button>
              </>
            )}
          </div>

          {/* Gallery Thumbnail Section */}
          {imageUrls.length > 1 && (
            <div className="p-4 bg-gray-100 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">More Photos</h3>
              <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-3">
                {imageUrls.map((src, i) => (
                  <div
                    key={i}
                    className={`relative w-full h-20 rounded-lg overflow-hidden cursor-pointer border-2 ${
                      i === currentImageIndex ? "border-blue-500 ring-2 ring-blue-300" : "border-transparent"
                    } hover:border-blue-400 transition-all duration-200`}
                    onClick={() => goToSlide(i)}
                  >
                    <img
                      src={`/images/thingstodo/${src}`}
                      alt={`${item.title} thumbnail ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {i === currentImageIndex && (
                      <div className="absolute inset-0 bg-blue-500 bg-opacity-20 flex items-center justify-center">
                        <CheckCircle className="w-6 h-6 text-white" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Content Section */}
          <div className="p-6">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">{item.title}</h1>
            <p className="text-gray-700 text-lg mb-4">{item.short_description}</p>
            <p className="text-gray-700 mb-6">{item.description}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-600">
              <div className="flex items-center gap-2">
                <Icon className="w-5 h-5 text-blue-600" />
                <span className="font-semibold">Category:</span> {item.category}
              </div>
              {item.location && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-green-600" />
                  <span className="font-semibold">Location:</span> {item.location}
                </div>
              )}
              {(item.open_time || item.close_time) && (
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-purple-600" />
                  <span className="font-semibold">Open Hours:</span> {item.open_time?.substring(0, 5)} - {item.close_time?.substring(0, 5)}
                </div>
              )}
              {item.recommended_time && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-orange-600" />
                  <span className="font-semibold">Recommended Time:</span> {item.recommended_time}
                </div>
              )}
              {item.distance_from_ella_km && (
                <div className="flex items-center gap-2">
                  <Car className="w-5 h-5 text-red-600" />
                  <span className="font-semibold">Distance from Ella:</span> {item.distance_from_ella_km} km
                </div>
              )}
              {item.entry_fee && item.entry_fee !== "0.00" && (
                <div className="flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-indigo-600" />
                  <span className="font-semibold">Entry Fee:</span> LKR {parseFloat(item.entry_fee).toFixed(2)}
                </div>
              )}
            </div>

            {/* Google Map Embed */}
            {item.google_map_link && (
              <div className="mt-8">
                <h3 className="text-xl font-bold text-gray-800 mb-3">Location on Map</h3>
                <div className="relative" style={{ paddingBottom: '56.25%', height: 0 }}>
                  <iframe
                    src={item.google_map_link}
                    width="600"
                    height="450"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="absolute top-0 left-0 w-full h-full rounded-lg shadow-md"
                    title={`Google Map for ${item.title}`}
                  ></iframe>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}